import { useEffect, useState } from 'react';
import { myMembershipsQuery, subscribe } from '@/lib/firestore';
import type { AccountMembership } from '@/schemas';

export type Membership = AccountMembership & { id: string };

export function useMyMemberships(uid: string | null): {
  memberships: Membership[];
  loading: boolean;
} {
  // Track which uid the cached data belongs to. Returning derived
  // values (rather than separate `loading`/`data` state) means a
  // render that fires immediately after uid changes — before the
  // effect has run — does NOT return the previous uid's data with
  // loading=false. That stale-state-during-render bug bit
  // RootRedirect (which routes by `memberships[0]?.id`) and the
  // AppShell membership guard: both saw an old uid's empty/old
  // memberships and made wrong routing decisions.
  const [state, setState] = useState<{ loadedUid: string | null; data: Membership[] }>({
    loadedUid: null,
    data: [],
  });
  useEffect(() => {
    if (!uid) {
      setState({ loadedUid: null, data: [] });
      return;
    }
    setState({ loadedUid: null, data: [] });
    const unsub = subscribe<AccountMembership>(myMembershipsQuery(uid), (rows) => {
      setState({ loadedUid: uid, data: rows });
    });
    return unsub;
  }, [uid]);
  return {
    memberships: state.loadedUid === uid ? state.data : [],
    loading: uid != null && state.loadedUid !== uid,
  };
}
